import { z } from 'zod';
import { Id } from '../primitives.js';

/**
 * Контракты модуля Patients. Одна схема — и форма на клиенте, и валидация на
 * сервере, и OpenAPI. Расходиться нечему.
 *
 * Документация модуля: docs/03-modules/02-patients.md
 */

export const PatientSex = z.enum(['male', 'female', 'other', 'unknown']);
export type PatientSex = z.infer<typeof PatientSex>;

/**
 * Источник обращения: откуда пациент узнал о клинике.
 *
 * Справочник арендатора, а не перечисление в схеме: каналы у клиник разные, и
 * «Инстаграм» вместо «Социальных сетей» — это переименование строки, а не
 * миграция. Неизменны коды — по ним сходятся отчёты.
 *
 * Приезжает на устройство целиком вместе с рабочим окном: карта заводится и без
 * связи, а без справочника её было бы не из чего заполнить.
 */
export const PatientSource = z.object({
  id: Id,
  tenantId: Id,
  code: z.string().min(1).max(50),
  name: z.string().min(1).max(100),
  /** Рекомендация и реклама без уточнения не отвечают, кого благодарить. */
  requiresDetails: z.boolean(),
  /** «Не указан» и «Перенос базы»: снять с учёта нельзя. */
  isSystem: z.boolean(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  rowVersion: z.number().int().nonnegative(),
});
export type PatientSource = z.infer<typeof PatientSource>;

export const Patient = z.object({
  id: Id,
  tenantId: Id,
  lastName: z.string().min(1).max(100),
  firstName: z.string().min(1).max(100),
  middleName: z.string().max(100).nullable(),
  birthDate: z.iso.date().nullable(),
  sex: PatientSex.default('unknown'),
  phone: z.string().min(5).max(32).nullable(),
  email: z.email().nullable(),
  /** Предупреждения у кресла: аллергии, инфекции — видны даже без медправ. */
  alerts: z.array(z.string().min(1).max(200)).default([]),
  /**
   * Откуда пришёл пациент. Пусто только у карт, заведённых до появления
   * справочника: в новых это обязательное поле.
   */
  sourceId: Id.nullable().default(null),
  sourceDetails: z.string().max(300).nullable().default(null),
  referredByPatientId: Id.nullable().default(null),
  /** Кого спрашивать, если пациент не пришёл. */
  responsibleAdminId: Id.nullable().default(null),
  /**
   * Карта, в которую эту влили как дубль. Заполнено — работа продолжается там,
   * и устройство обязано знать об этом без связи: иначе регистратура заведёт
   * приём в закрытую карту.
   */
  mergedIntoId: Id.nullable().default(null),
  rowVersion: z.number().int().nonnegative(),
  createdAt: z.iso.datetime(),
  deactivatedAt: z.iso.datetime().nullable(),
  /**
   * Семья: карты с одним и тем же значением, отдельной сущности у неё нет.
   * Необязательно, а не только `nullable`: в кэше устройств лежат карты,
   * пришедшие до появления поля, без ключа вовсе.
   */
  familyId: Id.nullable().optional(),
  /**
   * Номер карты в прежней программе — его называет пациент, перешедший из неё,
   * и его же показывает шапка карты. Необязательно по той же причине, что и
   * `familyId`: в кэше лежат карты, пришедшие без этого ключа.
   */
  externalId: z.string().nullable().optional(),
});
export type Patient = z.infer<typeof Patient>;

export const CreatePatientRequest = z.object({
  lastName: z.string().min(1).max(100),
  firstName: z.string().min(1).max(100),
  middleName: z.string().max(100).optional(),
  birthDate: z.iso.date().optional(),
  sex: PatientSex.optional(),
  phone: z.string().min(5).max(32).optional(),
  email: z.email().optional(),
  alerts: z.array(z.string().min(1).max(200)).max(20).optional(),
  /**
   * Источник обращения — обязателен, и это решение продукта, а не строгость
   * схемы. Без него нельзя посчитать, какой канал приводит пациентов, доходящих
   * до лечения, — то есть нельзя понять, куда клиника тратит деньги на
   * привлечение. Значение «не указан» в справочнике есть: оно попадает в отчёт
   * отдельной строкой, а не растворяется среди звонков.
   */
  sourceId: Id,
  /** Кто порекомендовал или какая реклама сработала. */
  sourceDetails: z.string().max(300).nullable().optional(),
  /** Рекомендатель из числа пациентов клиники — чтобы поблагодарить именно его. */
  referredByPatientId: Id.nullable().optional(),
  /**
   * Ответственный администратор. Пусто — тот, кто заводит карту: в подавляющем
   * большинстве случаев это он, и спрашивать об этом у стойки незачем.
   */
  responsibleAdminId: Id.nullable().optional(),
  /**
   * Сразу в семью: значение семьи другой карты — войти в неё, новое — завести
   * семью с этой карты.
   */
  familyId: Id.optional(),
});
export type CreatePatientRequest = z.infer<typeof CreatePatientRequest>;

/**
 * Изменение карты. Не `CreatePatientRequest.partial()`: в правке `null` значит
 * «стереть значение», и без этого ошибочно введённый телефон или отчество
 * остались бы в карте навсегда — исправить их было бы нечем. Отсутствие поля и
 * `null` — разные вещи, и сценарий на сервере различает их именно так.
 */
export const UpdatePatientRequest = z.object({
  lastName: z.string().min(1).max(100).optional(),
  firstName: z.string().min(1).max(100).optional(),
  middleName: z.string().max(100).nullable().optional(),
  birthDate: z.iso.date().nullable().optional(),
  sex: PatientSex.optional(),
  phone: z.string().min(5).max(32).nullable().optional(),
  email: z.email().nullable().optional(),
  alerts: z.array(z.string().min(1).max(200)).max(20).optional(),
  /**
   * Источник правится, но не всеми: это исправление ошибки ввода, а не обычное
   * поле карты. Переписанный задним числом источник обнуляет смысл цифр по
   * каналам, поэтому менять его может владелец или старший администратор, и
   * правка попадает в аудит.
   */
  sourceId: Id.optional(),
  sourceDetails: z.string().max(300).nullable().optional(),
  referredByPatientId: Id.nullable().optional(),
  /** Ответственный меняется вручную: пациента передали другому администратору. */
  responsibleAdminId: Id.nullable().optional(),
  /** Войти в семью — её значение; `null` — выйти из семьи. */
  familyId: Id.nullable().optional(),
});
export type UpdatePatientRequest = z.infer<typeof UpdatePatientRequest>;

/**
 * Поиск по картотеке клиники — по сети, минуя кэш устройства.
 *
 * На устройстве лежит рабочее окно: расписание на ближайшие дни, очередь
 * обзвона и карты, с которыми недавно работали. Пациента, лечившегося три года
 * назад, там нет, и без этого запроса найти его было бы нечем — а после
 * переноса базы из прежней программы в таком положении оказалось бы
 * большинство карт.
 *
 * Постраничной выдачи здесь нет намеренно: регистратор не листает результаты,
 * он уточняет запрос. Если нужного нет в первых двадцати строках, дело не в
 * странице, а в том, что искали «Иванов» там, где Ивановых полторы сотни.
 */
export const PATIENT_SEARCH_MIN_QUERY = 3;

export const PatientDirectoryQuery = z.object({
  q: z.string().min(PATIENT_SEARCH_MIN_QUERY).max(100),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type PatientDirectoryQuery = z.infer<typeof PatientDirectoryQuery>;

/** Строка результата: ровно то, чем одного человека отличают от другого. */
export const PatientDirectoryEntry = z.object({
  id: Id,
  lastName: z.string(),
  firstName: z.string(),
  middleName: z.string().nullable(),
  birthDate: z.iso.date().nullable(),
  phone: z.string().nullable(),
  /** Номер карты в прежней программе: по нему ищут те, кто перешёл с неё. */
  externalId: z.string().nullable(),
  deactivatedAt: z.iso.datetime().nullable(),
  /**
   * Семья найденной карты: добавить в семью — значит взять это значение или,
   * если его нет, завести новое на обе карты. Необязательно — сервер прежней
   * версии поля не присылает.
   */
  familyId: Id.nullable().optional(),
});
export type PatientDirectoryEntry = z.infer<typeof PatientDirectoryEntry>;

export const PatientDirectoryResponse = z.object({
  query: z.string(),
  entries: z.array(PatientDirectoryEntry),
});
export type PatientDirectoryResponse = z.infer<typeof PatientDirectoryResponse>;

/**
 * Пациенты по визитам — «все пациенты врача», «кто был у нас за неделю».
 *
 * По сети, как поиск по картотеке: на устройстве лежит рабочее окно (день назад
 * и неделя вперёд), а пациенты врача за год — история. Визит считается, если
 * его не отменили и пациент не пропустил его: записанные на будущие дни периода
 * входят — «пациенты врача на этой неделе» это и те, кто придёт в пятницу.
 *
 * Без отбора запрос не принимается: врач или хоть одна граница периода
 * обязательны, иначе это выгрузка всей картотеки одним ответом. Потолок строк —
 * по той же причине; выше него ответ честно говорит, что список обрезан, и
 * показывает пациентов с самыми свежими визитами.
 */
export const PATIENT_VISITS_LIMIT = 300;

export const PatientVisitsQuery = z
  .object({
    doctorId: Id.optional(),
    /** Начало периода — момент ISO: визиты с `startsAt >= from`. */
    from: z.iso.datetime().optional(),
    /** Конец периода — момент ISO, не включительно: начало дня после последнего выбранного. */
    to: z.iso.datetime().optional(),
  })
  .refine((query) => query.doctorId !== undefined || query.from !== undefined || query.to !== undefined, {
    message: 'Нужен врач или период: без отбора это выгрузка всей картотеки',
  });
export type PatientVisitsQuery = z.infer<typeof PatientVisitsQuery>;

export const PatientVisitsEntry = PatientDirectoryEntry.extend({
  /** Предупреждения карты — видны в списке до того, как пациента посадили в кресло. */
  alerts: z.array(z.string()),
  /** Визитов в отборе. */
  visitCount: z.number().int().positive(),
  firstVisitAt: z.iso.datetime(),
  lastVisitAt: z.iso.datetime(),
  /** Врачи визитов в отборе: без отбора по врачу их бывает несколько. */
  doctorIds: z.array(Id),
});
export type PatientVisitsEntry = z.infer<typeof PatientVisitsEntry>;

export const PatientVisitsResponse = z.object({
  entries: z.array(PatientVisitsEntry),
  /** Пациентов больше потолка: показаны те, у кого последний визит свежее. */
  truncated: z.boolean(),
});
export type PatientVisitsResponse = z.infer<typeof PatientVisitsResponse>;

/**
 * Перенос базы из прежней программы.
 *
 * Значения приходят такими, как лежали в файле, — строками. Приводит их к полям
 * карты сервер, а не клиент: разбор «07.03.84» и «8 (912) 345-67-89» — правило,
 * а правило в системе одно. Клиент показывает тот же результат заранее, вызывая
 * те же функции ядра, поэтому предпросмотр и запись не расходятся.
 */
export const PatientImportValues = z.object({
  /** Номер карты в прежней программе: по нему повторный импорт узнаёт своих. */
  externalId: z.string().max(64).optional(),
  lastName: z.string().max(200).optional(),
  firstName: z.string().max(200).optional(),
  middleName: z.string().max(200).optional(),
  /** ФИО одной колонкой: так устроено большинство выгрузок. */
  fullName: z.string().max(300).optional(),
  birthDate: z.string().max(40).optional(),
  sex: z.string().max(40).optional(),
  phone: z.string().max(40).optional(),
  email: z.string().max(200).optional(),
});
export type PatientImportValues = z.infer<typeof PatientImportValues>;

/** Колонки, которые система принимает из выгрузки. */
export type PatientImportField = keyof PatientImportValues;

/**
 * Размер одной посылки. Файл на пять тысяч карт уходит частями: одна
 * транзакция на весь файл держала бы таблицу пациентов заблокированной минутами,
 * а разорванное соединение отменяло бы всю работу целиком.
 */
export const PATIENT_IMPORT_BATCH = 500;

export const ImportPatientsRequest = z.object({
  /**
   * Откуда данные: имя прежней программы или файла. Попадает в карту пациента —
   * через год никто не вспомнит, откуда взялись эти записи, а спорную строку
   * надо будет с чем-то сверить.
   */
  source: z.string().min(1).max(120),
  /**
   * Проверка без записи. Отдельный признак, а не отдельный адрес: предпросмотр
   * обязан проходить ровно тот же путь, что и запись, иначе он показывает не то,
   * что произойдёт.
   */
  dryRun: z.boolean().default(false),
  rows: z
    .array(
      z.object({
        /** Номер строки в файле: по нему человек находит запись в исходнике. */
        line: z.number().int().min(1),
        /** UUIDv7 от клиента: повтор посылки не создаёт вторую карту. */
        id: Id,
        values: PatientImportValues,
      }),
    )
    .min(1)
    .max(PATIENT_IMPORT_BATCH),
});
export type ImportPatientsRequest = z.infer<typeof ImportPatientsRequest>;

export const PatientImportRowResult = z.object({
  line: z.number().int(),
  /**
   * `duplicate` — не ошибка, а нормальный исход повторного запуска: карта уже
   * заведена, и вторая ей не нужна.
   */
  status: z.enum(['created', 'duplicate', 'invalid']),
  patientId: Id.optional(),
  reason: z.string().optional(),
  /** Строка загружена, но часть значений не разобрана. */
  warnings: z.array(z.string()).optional(),
});
export type PatientImportRowResult = z.infer<typeof PatientImportRowResult>;

export const ImportPatientsResponse = z.object({
  dryRun: z.boolean(),
  created: z.number().int().nonnegative(),
  duplicates: z.number().int().nonnegative(),
  invalid: z.number().int().nonnegative(),
  rows: z.array(PatientImportRowResult),
});
export type ImportPatientsResponse = z.infer<typeof ImportPatientsResponse>;

/**
 * Согласия пациента.
 *
 * Три вида, и они не взаимозаменяемы. Обработка персональных данных —
 * основание работать с картой вообще (152-ФЗ). Рассылки — основание звонить и
 * писать; отзыв обязан немедленно остановить CRM. Медицинское вмешательство —
 * основание лечить (323-ФЗ), и подписывают его на конкретный план.
 */
export const PatientConsentKind = z.enum(['personal_data', 'marketing', 'treatment']);
export type PatientConsentKind = z.infer<typeof PatientConsentKind>;

/** Строка — акт, а не состояние: отзыв дописывается, а не правит прежнюю. */
export const PatientConsentAct = z.enum(['granted', 'withdrawn']);
export type PatientConsentAct = z.infer<typeof PatientConsentAct>;

/** Чем подтверждено. В клинике на три кресла бумага остаётся основным способом. */
export const PatientConsentChannel = z.enum(['paper', 'verbal', 'online']);
export type PatientConsentChannel = z.infer<typeof PatientConsentChannel>;

export const PatientConsent = z.object({
  id: Id,
  tenantId: Id,
  patientId: Id,
  kind: PatientConsentKind,
  act: PatientConsentAct,
  channel: PatientConsentChannel,
  /** Номер или название бланка: формы у клиник свои. */
  document: z.string().max(200).nullable().default(null),
  recordedBy: Id,
  /** Когда согласие получено, а не когда строка попала в базу. */
  actedAt: z.iso.datetime(),
  createdAt: z.iso.datetime(),
});
export type PatientConsent = z.infer<typeof PatientConsent>;

export const RecordConsentRequest = z.object({
  kind: PatientConsentKind,
  act: PatientConsentAct,
  channel: PatientConsentChannel,
  document: z.string().trim().max(200).optional(),
  actedAt: z.iso.datetime(),
});
export type RecordConsentRequest = z.infer<typeof RecordConsentRequest>;

/**
 * Заметка о пациенте. Append-only: исправление — новая заметка.
 *
 * Верхняя граница длины есть и она щедрая: заметка — это две строки о том, что
 * важно помнить у стойки, а не медицинская запись. Диагноз живёт в паспорте
 * зуба, где у него есть код и автор.
 */
export const PatientNote = z.object({
  id: Id,
  tenantId: Id,
  patientId: Id,
  text: z.string().min(1).max(2000),
  authorId: Id,
  createdAt: z.iso.datetime(),
});
export type PatientNote = z.infer<typeof PatientNote>;

export const CreatePatientNoteRequest = z.object({
  /**
   * Обрезка стоит до проверки длины намеренно: заметка из трёх пробелов — не
   * заметка, а мусор в карте. Правило живёт в схеме, а не в форме, потому что
   * та же схема разбирает операцию, пришедшую из очереди устройства.
   */
  text: z.string().trim().min(1).max(2000),
});
export type CreatePatientNoteRequest = z.infer<typeof CreatePatientNoteRequest>;

/**
 * Слияние дублей: в какую карту вливаем эту.
 *
 * Требует связи и отдельного права. Записи паспорта зуба при этом не
 * перепривязываются — они append-only по 323-ФЗ, и переписать у них пациента
 * нельзя даже правом базы. Вместо этого выжившая карта читает формулу вместе с
 * влитыми: история остаётся там, где её написали, а врач видит её целиком.
 */
export const MergePatientsRequest = z.object({
  /** Карта, которая останется в работе. */
  intoPatientId: Id,
});
export type MergePatientsRequest = z.infer<typeof MergePatientsRequest>;

export const MergePatientsResponse = z.object({
  survivorId: Id,
  mergedId: Id,
  /** Что переехало: по числам через год понятно, откуда у пациента эти приёмы. */
  moved: z.object({
    appointments: z.number().int().nonnegative(),
    treatmentPlans: z.number().int().nonnegative(),
    waitlistEntries: z.number().int().nonnegative(),
  }),
});
export type MergePatientsResponse = z.infer<typeof MergePatientsResponse>;
